import { Bell, Inbox, Filter, MoreVertical, CheckCheck, Trash2, Loader2 } from "lucide-react";
import { useState, useRef, useEffect, useMemo } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";
import { useDropdown } from "@/hooks/useDropdown";
import { useNotificationFilters } from "@/hooks/notifications";
import { groupNotificationsByTime } from "@/utils/notificationGrouping";
import { NotificationFilters } from "./NotificationFilters";
import { NotificationItem } from "./NotificationItem";

export const NotificationsPanel = () => {
  const { t } = useTranslation();
  const { isOpen, toggle, close, ref } = useDropdown();
  const [showFilters, setShowFilters] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const closeFilters = () => setShowFilters(false);
  const closeMenu = () => setShowMenu(false);
  useClickOutside(filterRef, closeFilters, showFilters);
  useClickOutside(menuRef, closeMenu, showMenu);

  const {
    notifications,
    stats,
    loading,
    totalCount,
    filters,
    hasActiveFilters,
    localSearch,
    loadMore,
    loadingMore,
    timeFilter,
    setTimeFilter,
    setSearch,
    setReadFilter,
    clearAllFilters,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleMarkAllAsRead,
    handleDelete,
    handleDeleteAll,
    handleNotificationClick,
  } = useNotificationFilters();

  const unreadCount = stats?.unread_count || 0;
  const notificationGroups = useMemo(
    () => groupNotificationsByTime(notifications, t),
    [notifications, t],
  );

  // Infinite scroll: load more when sentinel is visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !isOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, isOpen]);

  return (
    <div className="relative" ref={ref}>
      {/* Bell trigger */}
      <button
        onClick={toggle}
        aria-label={t("notifications.title")}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="relative p-2 rounded-md hover:bg-hover-secondary transition-colors"
      >
        <Bell size={18} className="text-text-secondary" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-violet-accent rounded-full border border-bg-primary" />
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          role="dialog"
          aria-label={t("notifications.title")}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              close();
            }
          }}
          tabIndex={-1}
          className="absolute right-0 top-full mt-2 w-80 max-h-[60vh] bg-bg-primary rounded-md shadow-lg border border-border-secondary flex flex-col z-popover"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-secondary">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-text-primary">
                {t("notifications.title")}
              </span>
              {unreadCount > 0 && (
                <span className="w-4 h-4 bg-violet-accent text-text-inverse text-2xs font-bold flex items-center justify-center rounded">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>

          {/* Search + Filter row */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border-secondary">
            <input
              type="text"
              placeholder={t("notifications.search_placeholder")}
              value={localSearch}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-2 py-1 text-xs border border-border-secondary rounded
                focus:outline-none focus:ring-1 focus:ring-text-primary
                bg-bg-primary placeholder:text-text-secondary"
            />
            <div className="relative" ref={filterRef}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "p-1.5 rounded border shadow-sm transition-colors bg-bg-primary hover:bg-bg-tertiary",
                  showFilters ? "border-border-focus" : "border-border-primary",
                )}
                title={t("notifications.filters")}
              >
                <Filter
                  size={12}
                  className={hasActiveFilters ? "text-text-secondary" : "text-text-tertiary"}
                />
              </button>
              {showFilters && (
                <div className="absolute right-0 top-full mt-1 bg-bg-primary rounded border border-border-secondary shadow-lg z-10">
                  <NotificationFilters
                    filters={filters}
                    timeFilter={timeFilter}
                    onReadFilterChange={setReadFilter}
                    onTimeFilterChange={setTimeFilter}
                    onClear={clearAllFilters}
                    hasActiveFilters={hasActiveFilters}
                  />
                </div>
              )}
            </div>
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className={cn(
                  "p-1.5 rounded border shadow-sm transition-colors bg-bg-primary hover:bg-bg-tertiary",
                  showMenu ? "border-border-focus" : "border-border-primary",
                )}
                title={t("notifications.options")}
              >
                <MoreVertical size={12} className="text-text-tertiary" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 bg-bg-primary rounded border border-border-secondary shadow-lg z-10 whitespace-nowrap">
                  <button
                    onClick={() => {
                      void handleMarkAllAsRead();
                      setShowMenu(false);
                    }}
                    disabled={unreadCount === 0}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCheck size={14} className="text-text-secondary" />
                    {t("notifications.mark_all_read")}
                  </button>
                  <button
                    onClick={() => {
                      void handleDeleteAll();
                      setShowMenu(false);
                    }}
                    disabled={totalCount === 0}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={14} className="text-text-secondary" />
                    {t("notifications.delete_all")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-xs text-text-secondary">{t("notifications.loading")}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Inbox size={20} className="mx-auto mb-2 text-text-secondary" />
                <p className="text-xs text-text-secondary">
                  {hasActiveFilters
                    ? t("notifications.no_results")
                    : t("notifications.empty_state")}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="mt-2 text-xs text-text-secondary hover:text-text-primary underline"
                  >
                    {t("notifications.clear_filters")}
                  </button>
                )}
              </div>
            ) : (
              <>
                {notificationGroups.map((group) => (
                  <div key={group.key}>
                    <div className="px-3 py-0.5">
                      <span className="text-3xs text-text-secondary uppercase tracking-wide">
                        {group.label}
                      </span>
                    </div>
                    {group.notifications.map((notification) => (
                      <NotificationItem
                        key={notification.uuid}
                        notification={notification}
                        onMarkAsRead={handleMarkAsRead}
                        onMarkAsUnread={handleMarkAsUnread}
                        onDelete={handleDelete}
                        onClick={() => {
                          handleNotificationClick(notification);
                          close();
                        }}
                      />
                    ))}
                  </div>
                ))}

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="h-1" />
                {loadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 size={14} className="animate-spin text-text-secondary" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
